import React, { FC } from 'react';
import styled from 'styled-components';
import { Button, Input, LoaderDots, ModalContent, ModalHeader, ModalTitle, Text } from 'components';
import { theme, toRem } from 'design';
import { IEmployee, IEmployerSite, IPOSProvider, ISiteIntegration } from 'types';
import { capitalize } from 'utils/strings';
import { ActionText, ContentFooter, Layout, ResultsWrapper, SearchBar, StyledSubtitle, Wrapper } from '../styles';
import EmployeeInfo from './EmployeeInfo';
import EmployeeList from './EmployeeList';

interface ISearchProps {
  dataCy: string;
  integration: ISiteIntegration;
  provider: IPOSProvider;
  isLoading?: boolean;
  site: IEmployerSite;
  employees: IEmployee[];
  selectedEmployee: IEmployee;
  onInvite(): void;
  onClose(): void;
  onSearchChange(): void;
  onSetSelectedEmployee(employee: IEmployee): void;
  onContinue(): void;
}

const StyledContentFooter = styled(ContentFooter)`
  align-items: start;
  border-top: ${toRem(1)} solid ${theme.color.neutral[100]};
  padding-top: ${toRem(16)};

  p {
    line-height: ${toRem(22)};
  }
`;

const NoResultsText = styled(Text)`
  color: ${theme.color.neutral[500]};
  margin: auto;
`;

const LoaderWrapper = styled.div`
  width: 100%;

  div {
    margin: auto;
  }
`;

const Search: FC<ISearchProps> = ({ provider, integration, site, employees, selectedEmployee, onInvite, isLoading, onSearchChange, onSetSelectedEmployee, onContinue, onClose, dataCy }: ISearchProps) => (
  <Layout isActive onClose={onClose} data-cy={`${dataCy}-search`} maxWidth={540}>
    <ModalHeader>
      <ModalTitle data-cy="modal-title">Connect {capitalize(integration.type)} Employee To Kickfin</ModalTitle>
      <StyledSubtitle data-cy="modal-subtitle">Create a connection between employees on your POS to Kickfin users.</StyledSubtitle>
    </ModalHeader>
    <ModalContent>
      <Wrapper>
        <EmployeeInfo name={provider.name} type={provider.type} employeeId={provider.customerEmployeeId} dataCy={dataCy} />
        <Wrapper>
          <SearchBar>
            <Text fontWeight="semibold" size="small">
              Search
            </Text>
            <Input onChange={onSearchChange} placeholder="Search by name" maxLength={255} />
          </SearchBar>
          {isLoading && (
            <LoaderWrapper>
              <LoaderDots />
            </LoaderWrapper>
          )}
          {!isLoading && employees && employees.length === 0 && (
            <>
              <ResultsWrapper>
                <NoResultsText fontWeight="semibold" size="small">
                  Sorry, no results found
                </NoResultsText>
              </ResultsWrapper>
            </>
          )}
          {!isLoading && employees && employees.length > 0 && (
            <>
              <ResultsWrapper>
                <Text fontWeight="semibold" size="small">
                  Results ({employees.length})
                </Text>
                <EmployeeList site={site} employees={employees} onSelect={onSetSelectedEmployee} selectedEmployee={selectedEmployee} integration={integration} dataCy={dataCy} />
              </ResultsWrapper>
              <Button appearance="primary" label="Continue" disabled={!selectedEmployee} onClick={onContinue} />
            </>
          )}
        </Wrapper>
      </Wrapper>
      <StyledContentFooter>
        <Text fontWeight="semibold" size="small">
          Not finding the correct Kickfin user?
        </Text>
        <ActionText size="small" onClick={onInvite}>
          Invite a new user
        </ActionText>
      </StyledContentFooter>
    </ModalContent>
  </Layout>
);

export default Search;
